import { Dispatch, useEffect, useState } from 'react';
import Link from 'next/link';
import { ApiRequest } from '@/utils/axios';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { TTab } from '@/pages/fairplay';
import { IUser } from '@/utils/types/User';
import { rarityColor } from '@/utils/rarity';
import { toFullSteamAvatar } from '@/utils/avatar';

interface Props {
    game: TTab;
    initialSecret: string | undefined;
    initialHash: string | undefined;
    initialBattleId?: string;
    initialBlock?: string;
    initialBlockId?: string;
    tab?: TTab;
    setTab: Dispatch<TTab>;
}

interface RichSkin {
    id: number;
    name: string;
    icon: string;
    priceUsd: number;
    colorHex: string;
}

interface WinnerData {
    id: number;
    user: IUser;
    hash: string;
    secret: string;
    amount?: number;
    users?: IUser[];
    skins?: Record<number, RichSkin[]>;
}

/** Result of the in-browser Case Battles (EOS block) verification. */
interface CaseBattlesProof {
    battleId: string;
    block: string;
    seed: string;
    digest: string;
    ticket: number;
}

const HomeIcon = (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 11l9-8 9 8" />
        <path d="M5 10v10h14V10" />
    </svg>
);

const ChevronIcon = (
    <svg
        className="pf-crumb-sep"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
    >
        <path d="M9 6l6 6-6 6" />
    </svg>
);

const ShieldIcon = (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 3l9 4v6c0 5-4 8-9 8s-9-3-9-8V7l9-4z" />
        <path d="M9 12l2 2 4-4" />
    </svg>
);

const CheckIcon = (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 12l4 4 10-10" />
    </svg>
);

const TABS: { id: TTab; label: string }[] = [
    { id: 'jackpot', label: 'Jackpot' },
    { id: 'coinflip', label: 'Coinflip' },
    { id: 'casebattles', label: 'Case Battles' },
];

/** SHA-256 → server-style verification reference (Jackpot / Coinflip). */
const PHP_SNIPPET = `<span class="tag">&lt;?php</span>
<span class="var">$secret</span>      <span class="op">=</span> <span class="str">'Type secret'</span>;
<span class="var">$round_hash</span>  <span class="op">=</span> <span class="str">'Type Round hash'</span>;
<span class="var">$seed</span>        <span class="op">=</span> <span class="var">$round_hash</span> <span class="op">.</span> <span class="var">$secret</span>;
<span class="fn">mt_srand</span>(<span class="fn">crc32</span>(<span class="var">$seed</span>));
<span class="var">$ticket</span>     <span class="op">=</span> <span class="fn">mt_rand</span>(<span class="num">1</span>, <span class="num">1000</span>) <span class="op">/</span> <span class="num">10</span>;
<span class="fn">var_dump</span>(<span class="var">$ticket</span>);
<span class="tag">?&gt;</span>`;

/** Case Battles use a public EOS block as the seed — the verification runs
 *  fully in the browser, so the reference snippet is the exact JS we run. */
const JS_SNIPPET = `<span class="key">const</span> <span class="var">seed</span>   <span class="op">=</span> <span class="var">blockId</span> <span class="op">+</span> <span class="str">'-'</span> <span class="op">+</span> <span class="var">battleId</span>;
<span class="key">const</span> <span class="var">buf</span>    <span class="op">=</span> <span class="key">await</span> <span class="fn">crypto</span>.subtle.<span class="fn">digest</span>(
                 <span class="str">'SHA-256'</span>, <span class="key">new</span> <span class="fn">TextEncoder</span>().<span class="fn">encode</span>(<span class="var">seed</span>));
<span class="key">const</span> <span class="var">hex</span>    <span class="op">=</span> [...<span class="key">new</span> <span class="fn">Uint8Array</span>(<span class="var">buf</span>)]
                 .<span class="fn">map</span>(<span class="var">b</span> <span class="op">=&gt;</span> <span class="var">b</span>.<span class="fn">toString</span>(<span class="num">16</span>).<span class="fn">padStart</span>(<span class="num">2</span>, <span class="str">'0'</span>)).<span class="fn">join</span>(<span class="str">''</span>);
<span class="key">const</span> <span class="var">ticket</span> <span class="op">=</span> <span class="fn">parseInt</span>(<span class="var">hex</span>.<span class="fn">slice</span>(<span class="num">0</span>, <span class="num">8</span>), <span class="num">16</span>) <span class="op">/</span> <span class="num">0xffffffff</span> <span class="op">*</span> <span class="num">100</span>;`;

/** Browser-side SHA-256 → lowercase hex. */
async function sha256Hex(input: string): Promise<string> {
    const data = new TextEncoder().encode(input);
    const buf = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

const CheckFairness = ({
    game,
    initialSecret,
    initialHash,
    initialBattleId,
    initialBlock,
    initialBlockId,
    tab,
    setTab,
}: Props) => {
    const [desc, setDesc] = useState<string>('');
    const [hash, setHash] = useState<string>(initialHash || '');
    const [secret, setSecret] = useState<string>(initialSecret || '');
    // Case Battles inputs (EOS block based).
    const [battleId, setBattleId] = useState<string>(initialBattleId || '');
    const [block, setBlock] = useState<string>(initialBlock || '');
    const [blockId, setBlockId] = useState<string>(initialBlockId || '');

    const [busy, setBusy] = useState<boolean>(false);
    const [result, setResult] = useState<{ kind: 'match' | 'mismatch'; text: string } | null>(null);
    const [winner, setWinner] = useState<WinnerData | null>(null);
    const [proof, setProof] = useState<CaseBattlesProof | null>(null);

    const activeTab: TTab = tab ?? 'jackpot';
    const isCaseBattles = activeTab === 'casebattles';

    function resetOutputs() {
        setResult(null);
        setWinner(null);
        setProof(null);
    }

    /** Jackpot / Coinflip — verified server-side via the fairplay API. */
    async function checkHashSecret() {
        if (!hash || !secret) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper
                    title={'Missing input'}
                    text={'Enter both hash and secret to verify.'}
                />
            );
            return;
        }
        setBusy(true);
        resetOutputs();

        try {
            const res = await ApiRequest.post('/fairplay/' + activeTab, { hash, secret });
            if (!res || !res.data) return;

            const { success, message, response } = res.data;

            if (!success && message) {
                setResult({ kind: 'mismatch', text: message });
                notify('error', <AlertIcon />, <NotifyWrapper title={'Error'} text={message} />);
                return;
            }

            setResult({
                kind: 'match',
                text: 'Hash matches secret — round was provably fair.',
            });

            const users: IUser[] | undefined =
                response.users ??
                ([response.winnerUser, response.loserUser].filter(Boolean) as IUser[]);

            setWinner({
                id: response.id,
                user: response.winnerUser,
                hash: response.hash,
                secret: response.secret,
                amount: response.prizeUsd ?? response.amount,
                users: users && users.length > 0 ? users : undefined,
                skins: response.skins,
            });
            setSecret('');
            setHash('');
        } finally {
            setBusy(false);
        }
    }

    /** Case Battles — verified fully in the browser from the public EOS block. */
    async function checkCaseBattles() {
        if (!battleId || !block || !blockId) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper
                    title={'Missing input'}
                    text={'Enter the battle ID, EOS block number and EOS block ID.'}
                />
            );
            return;
        }
        setBusy(true);
        resetOutputs();

        try {
            const seed = `${blockId.trim()}-${battleId.trim()}`;
            const digest = await sha256Hex(seed);
            const ticket = (parseInt(digest.slice(0, 8), 16) / 0xffffffff) * 100;

            setProof({
                battleId: battleId.trim(),
                block: block.trim(),
                seed,
                digest,
                ticket,
            });
            setResult({
                kind: 'match',
                text: 'Seed re-hashed in your browser — this is the exact draw used for the battle.',
            });
        } catch (e) {
            setResult({
                kind: 'mismatch',
                text: 'Could not hash the seed in this browser.',
            });
            notify('error', <AlertIcon />, <NotifyWrapper title={'Error'} text={String(e)} />);
        } finally {
            setBusy(false);
        }
    }

    function check() {
        if (isCaseBattles) return checkCaseBattles();
        return checkHashSecret();
    }

    async function getDesc() {
        try {
            const {
                data: {
                    response: { content },
                },
            } = await ApiRequest.get('/static-pages/fairplay');
            setDesc(content || '');
        } catch {
            /* keep empty */
        }
    }

    useEffect(() => {
        setHash(initialHash || '');
        setSecret(initialSecret || '');
    }, [initialSecret, initialHash]);

    useEffect(() => {
        if (initialBattleId) setBattleId(initialBattleId);
        if (initialBlock) setBlock(initialBlock);
        if (initialBlockId) setBlockId(initialBlockId);
    }, [initialBattleId, initialBlock, initialBlockId]);

    useEffect(() => {
        getDesc().then();
    }, []);

    useEffect(() => {
        if (tab !== game && tab !== undefined) {
            setSecret('');
            setHash('');
            setBattleId('');
            setBlock('');
            setBlockId('');
        }
        resetOutputs();
    }, [tab]);

    return (
        <div className="flex flex-col gap-3 min-w-0 animate-fluent-fadeup">
            <section className="surface pf-shell">
                <header className="pf-head">
                    <h1 className="pf-title">PROVABLY FAIR</h1>
                    <nav className="pf-crumbs" aria-label="Breadcrumb">
                        <Link href="/" className="pf-crumb-home" aria-label="Home">
                            {HomeIcon}
                        </Link>
                        {ChevronIcon}
                        <span className="pf-crumb-current">Provably Fair</span>
                    </nav>
                </header>

                <div className="pf-tabs" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            className={`pf-tab ${activeTab === t.id ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeTab === t.id}
                            onClick={() => setTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Server-managed Q&A (Jackpot / Coinflip). */}
                {!isCaseBattles && (
                    <div
                        className="pf-body"
                        data-game={activeTab}
                        dangerouslySetInnerHTML={{ __html: desc }}
                    />
                )}

                {/* Built-in Case Battles explainer (EOS block model). */}
                {isCaseBattles && <CaseBattlesInfo />}

                <div className="pf-code-wrap">
                    <pre
                        className="pf-code"
                        data-lang={isCaseBattles ? 'JS' : 'PHP'}
                        dangerouslySetInnerHTML={{
                            __html: isCaseBattles ? JS_SNIPPET : PHP_SNIPPET,
                        }}
                    />
                </div>

                <div className="pf-verify">
                    <div className="pf-verify-head">
                        {ShieldIcon}
                        <span>Verify a {isCaseBattles ? 'battle' : 'round'}</span>
                    </div>
                    <p className="pf-verify-sub">
                        {isCaseBattles
                            ? "Paste the battle ID together with the EOS block number and block ID shown in the battle's fairness panel. The seed is re-hashed with SHA-256 right here in your browser — no server involved."
                            : "Paste the round hash and the revealed secret. We'll re-hash the secret with SHA-256 and confirm the round was sealed before it ran."}
                    </p>

                    {isCaseBattles ? (
                        <div className="pf-verify-grid pf-verify-grid-3">
                            <label className="pf-field">
                                <span className="pf-field-label">Battle ID</span>
                                <input
                                    className="pf-input"
                                    type="text"
                                    value={battleId}
                                    onChange={(e) => setBattleId(e.target.value)}
                                    placeholder="e.g. 84213"
                                    autoComplete="off"
                                />
                            </label>
                            <label className="pf-field">
                                <span className="pf-field-label">EOS Block Number</span>
                                <input
                                    className="pf-input"
                                    type="text"
                                    value={block}
                                    onChange={(e) => setBlock(e.target.value)}
                                    placeholder="e.g. 348 921 044"
                                    autoComplete="off"
                                />
                            </label>
                            <label className="pf-field">
                                <span className="pf-field-label">EOS Block ID (seed)</span>
                                <input
                                    className="pf-input"
                                    type="text"
                                    value={blockId}
                                    onChange={(e) => setBlockId(e.target.value)}
                                    placeholder="14ca3f5b1f…"
                                    autoComplete="off"
                                />
                            </label>
                        </div>
                    ) : (
                        <div className="pf-verify-grid">
                            <label className="pf-field">
                                <span className="pf-field-label">Hash</span>
                                <input
                                    className="pf-input"
                                    type="text"
                                    value={hash}
                                    onChange={(e) => setHash(e.target.value)}
                                    placeholder="96f3e040a8706280…"
                                    autoComplete="off"
                                />
                            </label>
                            <label className="pf-field">
                                <span className="pf-field-label">Secret</span>
                                <input
                                    className="pf-input"
                                    type="text"
                                    value={secret}
                                    onChange={(e) => setSecret(e.target.value)}
                                    placeholder="server-revealed seed"
                                    autoComplete="off"
                                />
                            </label>
                        </div>
                    )}

                    <button type="button" className="pf-check" onClick={check} disabled={busy}>
                        {CheckIcon}
                        {isCaseBattles ? 'Verify battle' : 'Check fair game'}
                    </button>

                    {result && (
                        <div className={`fp-result ${result.kind}`}>
                            {result.kind === 'match' ? (
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 12l4 4 10-10" />
                                </svg>
                            ) : (
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M9 9l6 6M15 9l-6 6" />
                                </svg>
                            )}
                            <span>{result.text}</span>
                        </div>
                    )}
                </div>
            </section>

            {winner && <WinnerBlock winner={winner} />}
            {proof && <CaseBattlesProofBlock proof={proof} />}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Case Battles explainer (no server content exists for this mode)    */
/* ------------------------------------------------------------------ */
const CaseBattlesInfo = () => (
    <div className="pf-body">
        <div className="pf-qa">
            <h2 className="pf-q">How are Case Battles provably fair?</h2>
            <p className="pf-a muted">
                Every battle is bound to a future, public <b>EOS blockchain block</b> the moment it
                is created. Nobody — not you, not us — can predict that block&apos;s hash in advance,
                and once it is produced it can never be changed. That public hash is the only source
                of randomness used to pull every item.
            </p>
        </div>
        <div className="pf-qa">
            <h2 className="pf-q">How do I check a finished battle?</h2>
            <p className="pf-step">
                1. Open the battle and its <b>Fairness</b> panel to copy the <b>Battle ID</b>,{' '}
                <b>EOS Block Number</b> and <b>EOS Block ID</b>.
            </p>
            <p className="pf-step">
                2. Look the block up on any public EOS explorer and confirm the block ID matches.
            </p>
            <p className="pf-step">
                3. Paste the three values below. Your browser re-hashes the seed with SHA-256 and
                derives the exact draw ticket — locally, with the code shown above.
            </p>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/* Case Battles verification output                                   */
/* ------------------------------------------------------------------ */
const CaseBattlesProofBlock = ({ proof }: { proof: CaseBattlesProof }) => {
    const shortDigest =
        proof.digest.length > 40 ? proof.digest.slice(0, 32) + '…' + proof.digest.slice(-6) : proof.digest;

    return (
        <section className="surface fp-confirmed relative mt-4 px-7 pt-7 pb-6 max-md:px-4">
            <div className="fp-conf-tab">FAIRPLAY VERIFIED</div>

            <div className="flex flex-col items-center gap-2 mb-[18px] mt-1">
                <div className="pf-cb-shield">{ShieldIcon}</div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-text_4 font-bold">
                    Provably-fair draw ticket
                </div>
                <div className="pf-cb-ticket font-chat-nick tabular-nums">
                    {proof.ticket.toFixed(4)}
                </div>
                <div className="text-[12px] text-text_3 text-center max-w-[420px] leading-relaxed">
                    Re-derived in your browser from the public EOS block. Anyone pasting the same
                    block&nbsp;ID and battle&nbsp;ID gets this identical value.
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-2.5">
                <div className="fp-conf-pill">
                    Battle ID: <b>{proof.battleId}</b>
                </div>
                <div className="fp-conf-pill">
                    EOS Block: <b>{proof.block}</b>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
                <div className="fp-conf-pill" title={proof.seed}>
                    Seed: <b>{proof.seed}</b>
                </div>
                <div className="fp-conf-pill" title={proof.digest}>
                    SHA-256: <b>{shortDigest}</b>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------ */
/* Jackpot / Coinflip winner reveal                                   */
/* ------------------------------------------------------------------ */
const WinnerBlock = ({ winner }: { winner: WinnerData }) => {
    const shortHash =
        winner.hash && winner.hash.length > 28 ? winner.hash.slice(0, 24) + '…' : winner.hash;
    const shortSecret =
        winner.secret && winner.secret.length > 28
            ? winner.secret.slice(0, 24) + '…'
            : winner.secret;

    const allSkins: RichSkin[] = winner.skins ? Object.values(winner.skins).flat() : [];

    return (
        <section className="surface fp-confirmed relative mt-4 px-7 pt-7 pb-6 max-md:px-4">
            <div className="fp-conf-tab">FAIRPLAY CONFIRMED</div>

            <div className="flex flex-col items-center gap-1 mb-[18px]">
                <div className="fp-conf-winner-ava">
                    <span className="fp-conf-winner-ribbon">WINNER</span>
                    {winner.user?.avatar ? (
                        <img
                            src={toFullSteamAvatar(winner.user.avatar)}
                            alt={winner.user.name}
                            width={86}
                            height={86}
                            className="rounded-[26%] w-full h-full object-cover"
                        />
                    ) : (
                        <div className="abstract-avatar aa-square w-full h-full" />
                    )}
                </div>
                <div className="mt-2.5 text-[18px] font-extrabold font-chat-nick text-accent_5 [text-shadow:0_0_16px_rgba(255,194,77,0.40)]">
                    {winner.user?.name || '—'}
                </div>
                {typeof winner.amount === 'number' && (
                    <div className="text-sm font-bold text-text_main mt-0.5 tabular-nums">
                        Won <span className="text-accent_5">${winner.amount.toFixed(2)}</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-[18px]">
                <div className="fp-conf-pill">
                    Round ID: <b>{winner.id}</b>
                </div>
                <div className="fp-conf-pill" title={winner.hash}>
                    Hash: <b>{shortHash || '—'}</b>
                </div>
                <div className="fp-conf-pill" title={winner.secret}>
                    Secret: <b>{shortSecret || '—'}</b>
                </div>
            </div>

            {allSkins.length > 0 && (
                <>
                    <div className="text-[11px] uppercase tracking-[0.10em] text-text_4 font-bold mb-2.5">
                        Won skins ({allSkins.length})
                    </div>
                    <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(80px,1fr))]">
                        {allSkins.map((skin) => (
                            <SkinTile key={skin.id} skin={skin} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};

const SkinTile = ({ skin }: { skin: RichSkin }) => {
    const rarity = rarityColor(skin.colorHex);
    return (
        <div
            className="fp-conf-skin"
            style={{ ['--rarity' as string]: rarity } as React.CSSProperties}
            title={`${skin.name} — $${skin.priceUsd.toFixed(2)}`}
        >
            {skin.icon ? (
                <img
                    src={skin.icon}
                    alt={skin.name}
                    width={42}
                    height={42}
                    className="object-contain w-[42px] h-[42px]"
                    draggable={false}
                />
            ) : (
                <div className="w-[42px] h-[42px] bg-layer_3 rounded" />
            )}
            <div className="text-[11.5px] font-bold font-chat-nick text-accent_5 mt-1 tabular-nums">
                ${skin.priceUsd.toFixed(2)}
            </div>
            <div className="text-[10.5px] text-text_3 mt-0.5 text-center whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                {skin.name}
            </div>
        </div>
    );
};

export default CheckFairness;
